(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const api = path => path.replace(/\/$/, '') + '.js';
  const errorText = () => document.body.dataset.error;
  let lastCartTrigger, cartBusy = false, variantRequest, toastTimer;
  const notify = message => {
    const toast = $('#StoreToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3500);
  };
  const showCart = trigger => {
    const drawer = $('#CartDrawer');
    if (!drawer || !drawer.showModal) { location.assign(document.body.dataset.cartUrl); return; }
    if (trigger) lastCartTrigger = trigger;
    if (!drawer.open) drawer.showModal();
    document.body.classList.add('scroll-locked');
    $('[data-cart-close]', drawer)?.focus();
  };
  const hideCart = () => {
    $('#CartDrawer')?.close();
    document.body.classList.remove('scroll-locked');
    if (lastCartTrigger?.isConnected) lastCartTrigger.focus();
  };
  const replaceCart = (html, count) => {
    if (!html) throw new Error(errorText());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = $('#CartDrawer', doc);
    const drawer = $('#CartDrawer');
    if (!content || !drawer) throw new Error(errorText());
    drawer.innerHTML = content.innerHTML;
    $$('[data-cart-count]').forEach(el => { el.textContent = count; });
  };
  const refreshCart = async () => {
    const cartUrl = new URL(document.body.dataset.cartUrl, location.origin);
    const sectionUrl = new URL(cartUrl);
    sectionUrl.searchParams.set('section_id', 'cart-drawer');
    const [cartResponse, sectionResponse] = await Promise.all([
      fetch(api(cartUrl.pathname), { credentials: 'same-origin' }),
      fetch(sectionUrl, { credentials: 'same-origin' })
    ]);
    if (!cartResponse.ok || !sectionResponse.ok) throw new Error(errorText());
    const cart = await cartResponse.json();
    replaceCart(await sectionResponse.text(), cart.item_count);
  };
  const changeCart = async (key, quantity) => {
    if (cartBusy) return;
    cartBusy = true;
    const drawer = $('#CartDrawer');
    const scrollTop = $('.drawer-body', drawer)?.scrollTop || 0;
    const inputId = document.activeElement?.id;
    drawer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(api(document.body.dataset.cartChangeUrl), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: document.body.dataset.cartUrl })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || errorText());
      if (result.sections?.['cart-drawer']) replaceCart(result.sections['cart-drawer'], result.item_count);
      else await refreshCart();
      $('.drawer-body', drawer).scrollTop = scrollTop;
      const focusTarget = inputId && document.getElementById(inputId);
      (focusTarget || $('[data-cart-close]', drawer)).focus({ preventScroll: true });
    } catch (error) {
      const node = $('[data-cart-error]', drawer);
      if (node) { node.textContent = error.message || errorText(); node.hidden = false; }
      try { await refreshCart(); notify(error.message || errorText()); } catch { notify(errorText()); }
    } finally { cartBusy = false; drawer.removeAttribute('aria-busy'); }
  };
  const setMedia = (gallery, id) => {
    $$('[data-media-id]', gallery).forEach(el => {
      const active = el.dataset.mediaId === id;
      el.classList.toggle('is-active', active);
      if (!active) {
        $$('video', el).forEach(v => v.pause());
        $$('iframe', el).forEach(frame => {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          frame.contentWindow?.postMessage({ method: 'pause' }, '*');
        });
      }
    });
    $$('[data-media-target]', gallery).forEach(el => {
      const active = el.dataset.mediaTarget === id;
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
    });
  };
  const selectVariant = async link => {
    const section = link.closest('[data-product-section]');
    if (!section) return;
    variantRequest?.abort();
    variantRequest = new AbortController();
    const target = new URL(link.href);
    target.searchParams.set('section_id', section.dataset.sectionId);
    section.setAttribute('aria-busy', 'true');
    $('.product-form', section)?.classList.add('loading');
    try {
      const response = await fetch(target, { signal: variantRequest.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(errorText());
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = $('[data-product-section]', doc);
      if (!fresh) throw new Error(errorText());
      const previousQuantity = $('[name="quantity"]', section)?.value;
      section.replaceWith(fresh);
      const qty = $('[name="quantity"]', fresh);
      if (qty && previousQuantity && Number(previousQuantity) >= Number(qty.min || 1)) {
        const value = Number(previousQuantity), step = Number(qty.step || 1), min = Number(qty.min || 1);
        if ((!qty.max || value <= Number(qty.max)) && (value - min) % step === 0) qty.value = previousQuantity;
      }
      const next = new URL(fresh.dataset.currentUrl || link.href, location.origin);
      if (!next.searchParams.has('variant')) next.searchParams.set('option_values', new URL(link.href).searchParams.get('option_values'));
      history.replaceState({}, '', next.pathname + next.search);
      $(`[data-option-id="${CSS.escape(link.dataset.optionId)}"]`, fresh)?.focus({ preventScroll: true });
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      initialize(fresh);
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(link.href);
    } finally {
      if (section.isConnected) { section.removeAttribute('aria-busy'); $('.product-form', section)?.classList.remove('loading'); }
    }
  };
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    if (form.dataset.pending === 'true') return;
    const button = $('button[type="submit"]', form);
    const errorNode = $('[data-form-error]', form);
    if (!form.reportValidity() || button?.disabled) return;
    form.dataset.pending = 'true';
    const body = new FormData(form);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorNode) errorNode.hidden = true;
    let added = false;
    try {
      const response = await fetch(api(document.body.dataset.cartAddUrl), { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || errorText());
      added = true;
      await refreshCart();
      showCart(button);
      notify(document.body.dataset.added);
    } catch (error) {
      if (added) location.assign(document.body.dataset.cartUrl);
      else if (errorNode) { errorNode.textContent = error.message || errorText(); errorNode.hidden = false; }
      else notify(error.message || errorText());
    } finally {
      form.dataset.pending = 'false';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-cart-open]');
    if (open && !event.metaKey && !event.ctrlKey && !event.shiftKey) { event.preventDefault(); showCart(open); return; }
    if (event.target.closest('[data-cart-close]')) { hideCart(); return; }
    if (event.target === $('#CartDrawer')) { const rect = event.target.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right) hideCart(); }
    const stepper = event.target.closest('[data-quantity-step]');
    if (stepper) {
      const input = $('input', stepper.closest('.quantity-picker'));
      if (stepper.closest('#CartDrawer') && cartBusy) return;
      try { Number(stepper.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown(); } catch { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeCart(remove.dataset.key, 0); }
    const media = event.target.closest('[data-media-target]');
    if (media) { event.preventDefault(); setMedia(media.closest('[data-gallery]'), media.dataset.mediaTarget); }
    const variant = event.target.closest('[data-option-link]');
    if (variant && !event.metaKey && !event.ctrlKey && !event.shiftKey) { event.preventDefault(); selectVariant(variant); }
    $$('.header-search[open],.nav-dropdown[open],.mobile-menu[open],.filter[open]').forEach(details => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity]');
    if (input && input.checkValidity() && input.value !== '') changeCart(input.dataset.key, Number(input.value));
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    $$('.header-search[open],.nav-dropdown[open],.mobile-menu[open],.filter[open]').forEach(el => { el.removeAttribute('open'); $('summary', el)?.focus(); });
  });
  $('#CartDrawer')?.addEventListener('close', () => {
    document.body.classList.remove('scroll-locked');
    if (lastCartTrigger?.isConnected) lastCartTrigger.focus();
  });
  const worldObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.filter(e => e.isIntersecting).forEach(({ target }) => {
      worldObserver.unobserve(target);
      import(target.dataset.sceneUrl).then(module => { if (target.isConnected) module.mount(target); }).catch(() => {});
    });
  }, { rootMargin: '100px' }) : null;
  const recommendationObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.filter(e => e.isIntersecting).forEach(async ({ target }) => {
      recommendationObserver.unobserve(target);
      try {
        const response = await fetch(target.dataset.url);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh?.innerHTML.trim() && target.isConnected) target.innerHTML = fresh.innerHTML;
      } catch {}
    });
  }, { rootMargin: '300px' }) : null;
  function initialize(root = document) {
    $$('[data-scene-url]:not([data-scene-initialized])', root).forEach(el => {
      el.dataset.sceneInitialized = 'true';
      worldObserver?.observe(el);
    });
    $$('[data-recommendations]:not([data-rec-initialized])', root).forEach(el => {
      el.dataset.recInitialized = 'true';
      recommendationObserver?.observe(el);
    });
    $$('[data-country-select]:not([data-initialized])', root).forEach(country => {
      country.dataset.initialized = 'true';
      const province = document.getElementById(country.dataset.provinceId);
      if (country.dataset.default) country.value = country.dataset.default;
      const update = () => {
        let provinces = [];
        try { provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
        province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
        if (province.dataset.default) province.value = province.dataset.default;
        province.closest('[data-province-wrap]').hidden = !provinces.length;
      };
      update();
      country.addEventListener('change', update);
    });
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => $$('[data-world]', event.target).forEach(el => el.dispatchEvent(new Event('scene:dispose'))));
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart().catch(() => {}); });
  initialize();
  const offer = document.querySelector('[data-conversion-offer]');
  if (offer) {
    const key = 'threesheep_mystery_offer_seen_forever_v3';
    const closeOffer = () => {
      offer.classList.remove('is-visible');
      document.body.classList.remove('scroll-locked');
      try { localStorage.setItem(key, '1'); } catch {}
    };
    offer.querySelectorAll('[data-offer-close]').forEach(el => el.addEventListener('click', closeOffer));
    let seen = false; try { seen = localStorage.getItem(key) === '1'; } catch {}
    if (!seen) {
      window.setTimeout(() => {
        offer.classList.add('is-visible');
        document.body.classList.add('scroll-locked');
        offer.querySelector('[data-mystery-tile]')?.focus();
      }, Number(offer.dataset.delay || 6000));
    }

    const tiles = [...offer.querySelectorAll('[data-mystery-tile]')];
    let revealed = false;
    tiles.forEach((tile, chosenIndex) => tile.addEventListener('click', () => {
      if (revealed) return;
      revealed = true;
      try { localStorage.setItem(key, '1'); } catch {}
      const otherOffers = ['10% OFF', 'NO OFFER'];
      if (Math.random() > .5) otherOffers.reverse();
      let oi = 0;
      tiles.forEach((t, i) => {
        const label = i === chosenIndex ? '25% OFF' : otherOffers[oi++];
        t.querySelector('.mystery-tile__back').textContent = label;
        if (i === chosenIndex) t.classList.add('is-winner');
        window.setTimeout(() => t.classList.add('is-revealed'), Math.abs(i - chosenIndex) * 130);
      });
      window.setTimeout(() => {
        offer.querySelector('[data-offer-pick-stage]')?.classList.add('is-leaving');
        window.setTimeout(() => {
          const pick = offer.querySelector('[data-offer-pick-stage]');
          const won = offer.querySelector('[data-offer-won-stage]');
          if (pick) pick.hidden = true;
          if (won) { won.hidden = false; requestAnimationFrame(() => won.classList.add('is-visible')); }
        }, 350);
      }, 1050);
    }));

    offer.querySelector('[data-copy-coupon]')?.addEventListener('click', async e => {
      const code = offer.dataset.code || 'THREESHEEP20';
      try { await navigator.clipboard.writeText(code); e.currentTarget.textContent = 'Copied'; }
      catch { e.currentTarget.textContent = code; }
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && offer.classList.contains('is-visible')) closeOffer(); });
  }

  // Premium same-site page transitions. Modified clicks and editor links are left alone.
  document.body.classList.add('ts-page-enter');
  window.setTimeout(() => document.body.classList.remove('ts-page-enter'), 650);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target === '_blank' || link.hasAttribute('download') || link.dataset.noTransition !== undefined) return;
    let url; try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin || url.pathname === location.pathname && url.search === location.search && url.hash) return;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    event.preventDefault();
    document.documentElement.classList.add('ts-page-leaving');
    window.setTimeout(() => { location.href = url.href; }, 250);
  });
  window.addEventListener('pageshow', () => document.documentElement.classList.remove('ts-page-leaving'));
})();
